import React, { useState, useRef, useEffect } from 'react';
import { FaPalette, FaUndo, FaSlidersH, FaEye, FaPaintBrush } from 'react-icons/fa';
import { FiCoffee, FiGift } from 'react-icons/fi';
import { BsToggleOff, BsToggleOn } from 'react-icons/bs';
import { MdColorLens } from 'react-icons/md';
import { useCreateAssetsMutation } from '~/generated/hooks';
import { FormSection } from './FormSection';
import { isArray } from 'lodash';
import { useNotification } from '@refinedev/core';
import type { UseFormRegister, FieldErrors, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { createDynamicPreviewData } from '../config/test-data';
import themeConfig from '~/components/StampCard/themes.json';
import StampCardIframe from '~/components/StampCard/StampCardIframe';

interface StampTemplateFormData {
  iconUrl?: string;
  iconFilledUrl?: string;
  iconWithRewardUrl?: string;
  iconWithRewardFilledUrl?: string;
  metadata?: {
    theme?: {
      cardBackground?: string;
      textAndBorderColor?: string;
      claimedStampColor?: string;
      nextStampHighlight?: string;
      checkmarkColor?: string;
      borderPattern?: 'solid' | 'dashed' | 'dotted';
      // Legacy naming (for compatibility)
      stampCardBackgroundColor?: string;
      stampCardTextColor?: string;
      stampBorderColor?: string;
    };
    // Keep backward compatibility for old structure
    stampCardBackgroundColor?: string;
    stampCardTextColor?: string;
    stampBorderColor?: string;
  };
  [key: string]: any;
}

interface VisualDesignSectionProps {
  register: UseFormRegister<StampTemplateFormData>;
  errors: FieldErrors<StampTemplateFormData>;
  defaultValues?: Partial<StampTemplateFormData>;
  setValue: UseFormSetValue<StampTemplateFormData>;
  watch: UseFormWatch<StampTemplateFormData>;
  testScenario?: 'default' | 'almost-complete' | 'milestone-ready' | 'all-milestones' | 'near-expiry';
  setTestScenario?: (scenario: 'default' | 'almost-complete' | 'milestone-ready' | 'all-milestones' | 'near-expiry') => void;
}

export const VisualDesignSection: React.FC<VisualDesignSectionProps> = ({
  register,
  errors: _errors,
  defaultValues = {},
  setValue,
  watch,
  testScenario = 'default',
  setTestScenario,
}) => {
  const fileInputRefs = {
    default: useRef<HTMLInputElement>(null),
    collected: useRef<HTMLInputElement>(null),
    reward: useRef<HTMLInputElement>(null),
    reward_claimed: useRef<HTMLInputElement>(null),
  };

  // Watch form values
  const iconUrl = watch?.('iconUrl');
  const iconFilledUrl = watch?.('iconFilledUrl');
  const iconWithRewardUrl = watch?.('iconWithRewardUrl');
  const iconWithRewardFilledUrl = watch?.('iconWithRewardFilledUrl');
  
  // Watch theme metadata with proper defaults - support both old and new structure
  const metadata = watch?.('metadata') || defaultValues?.metadata || {};
  // Check if theme has actual values, not just an empty object
  const watchedThemeRaw = watch?.('metadata.theme') || metadata.theme || {};
  const hasValidTheme = watchedThemeRaw && watchedThemeRaw.cardBackground;
  const watchedTheme = hasValidTheme ? watchedThemeRaw : themeConfig.presets.classic.theme;
  
  console.log({ watchedTheme})
  const [uploadingType, setUploadingType] = useState<string | null>(null);
  const { open: openNotification } = useNotification();
  const [resetCounter, setResetCounter] = useState(0);
  const [selectedPreset, setSelectedPreset] = useState<string>('custom');

  const cardBackground = watchedTheme.cardBackground;
  const textAndBorderColor = watchedTheme.textAndBorderColor;
  const claimedStampColor = watchedTheme.claimedStampColor;
  const nextStampHighlight = watchedTheme.nextStampHighlight;
  const checkmarkColor = watchedTheme.checkmarkColor;
  const borderPattern = watchedTheme.borderPattern;

  // Initialize metadata values from defaultValues if they exist
  useEffect(() => {
    // Only set defaults for new templates (when there's no existing metadata)
    if (!defaultValues?.metadata && !watch?.('metadata.theme.cardBackground')) {
      // Set Classic Coffee as default for new templates
      const classicTheme = themeConfig.presets.classic.theme;
      setValue('metadata.theme.cardBackground', classicTheme.cardBackground);
      setValue('metadata.theme.textAndBorderColor', classicTheme.textAndBorderColor);
      setValue('metadata.theme.claimedStampColor', classicTheme.claimedStampColor);
      setValue('metadata.theme.nextStampHighlight', classicTheme.nextStampHighlight);
      setValue('metadata.theme.checkmarkColor', classicTheme.checkmarkColor);
      setValue('metadata.theme.borderPattern', classicTheme.borderPattern as 'dashed' | 'solid' | 'dotted');
    }
  }, []); // Empty dependency array - only run once on mount

  // Calculate earned stamps based on test scenario
  const getEarnedStampsForScenario = (total: number) => {
    switch (testScenario) {
      case 'almost-complete':
        return total - 1;
      case 'milestone-ready':
        return Math.floor(total / 2);
      case 'all-milestones':
        return total;
      case 'near-expiry':
        return Math.floor(total * 0.7);
      default:
        return 3;
    }
  };

  // Preview controls state  
  const [previewControls, setPreviewControls] = useState({
    earnedStamps: getEarnedStampsForScenario(10),
    showMilestones: true,
    scenario: testScenario,
    totalStamps: 10,
    showPreviewControls: false
  });

  // Update earned stamps when test scenario changes
  useEffect(() => {
    setPreviewControls(prev => ({
      ...prev,
      earnedStamps: getEarnedStampsForScenario(prev.totalStamps),
      scenario: testScenario
    }));
  }, [testScenario]);

  // Use createAssets mutation for uploading images
  const { mutate: createAssets } = useCreateAssetsMutation({
    onSuccess: (data, variables) => {
      const tag = isArray(variables.input)
        ? variables.input[0].tags?.[0]
        : variables.input.tags?.[0];
      const results = data.createAssets;
      if (results && results.length > 0) {
        const asset = results[0];
        if ('source' in asset) {
          // Determine which field to update based on tag
          switch (tag) {
            case 'stamp-icon-default':
              setValue('iconUrl', asset.source);
              break;
            case 'stamp-icon-collected':
              setValue('iconFilledUrl', asset.source);
              break;
            case 'stamp-icon-reward':
              setValue('iconWithRewardUrl', asset.source);
              break;
            case 'stamp-icon-reward-claimed':
              setValue('iconWithRewardFilledUrl', asset.source);
              break;
          }
          setUploadingType(null);
          openNotification?.({
            type: 'success',
            message: 'Image uploaded successfully',
          });
        } else if ('errorCode' in asset) {
          setUploadingType(null);
          openNotification?.({
            type: 'error',
            message: 'Failed to upload image',
            description: asset.message,
          });
        }
      }
    },
    onError: (error) => {
      setUploadingType(null);
      openNotification?.({
        type: 'error',
        message: 'Failed to create asset',
        description: error?.message || 'An unexpected error occurred',
      });
    },
  });

  const handleIconClick = (type: keyof typeof fileInputRefs) => {
    fileInputRefs[type].current?.click();
  };

  const handleFileSelect = (type: string, files: FileList | null) => {
    if (files && files.length > 0) {
      const file = files[0];
      setUploadingType(type);
      createAssets({
        input: [
          {
            file: file,
            tags: [`stamp-icon-${type.replace('_', '-')}`],
          },
        ],
      });
    }
  };

  const handleReset = () => {
    // Clear the icon values
    setValue('iconUrl', undefined, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
    setValue('iconFilledUrl', undefined, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
    setValue('iconWithRewardUrl', undefined, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
    setValue('iconWithRewardFilledUrl', undefined, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
    setResetCounter(prev => prev + 1); // Force re-render of preview
    
    // Force a form re-render by triggering validation
    setTimeout(() => {
      setValue('iconUrl', '');
      setValue('iconFilledUrl', '');
      setValue('iconWithRewardUrl', '');
      setValue('iconWithRewardFilledUrl', '');
    }, 0);
  };

  const iconConfigs = [
    {
      id: 'default',
      label: 'Default',
      value: iconUrl,
      bgColor: 'bg-gray-100',
      labelColor: 'bg-gray-200 text-gray-700',
      defaultIcon: <FiCoffee className="text-gray-300 text-3xl" />,
    },
    {
      id: 'collected',
      label: 'Collected',
      value: iconFilledUrl,
      bgColor: '',
      labelColor: 'bg-green-200 text-green-800',
      defaultIcon: <FiCoffee className="text-gray-300 text-3xl" />,
    },
    {
      id: 'reward',
      label: 'Reward',
      value: iconWithRewardUrl,
      bgColor: 'bg-yellow-50',
      labelColor: 'bg-yellow-200 text-yellow-800',
      defaultIcon: <FiGift className="text-yellow-600 text-3xl" />,
    },
    {
      id: 'reward_claimed',
      label: 'Reward Claimed',
      value: iconWithRewardFilledUrl,
      bgColor: 'bg-yellow-400',
      labelColor: 'bg-yellow-300 text-yellow-900',
      defaultIcon: <FiGift className="text-white text-3xl" />,
    },
  ];

  return (
    <FormSection
      icon={
        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center text-white">
          <FaPalette className="text-xl" />
        </div>
      }
      title="Visual Design"
    >
      <div className="space-y-6">
        {/* Icon Customizer Card */}
        {/* Preview Card */}
        <div className="space-y-6">
          {/* Preview Header with Controls Toggle */}
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <FaEye className="text-blue-600" />
              Preview
            </h3>
            <button
              type="button"
              onClick={() => setPreviewControls(prev => ({ ...prev, showPreviewControls: !prev.showPreviewControls }))}
              className="flex items-center gap-2 px-3 py-2 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
            >
              <FaSlidersH className="text-xs" />
              {previewControls.showPreviewControls ? 'Hide' : 'Show'} Controls
            </button>
          </div>

          {/* Interactive Preview Controls */}
          {previewControls.showPreviewControls && (
            <div className="bg-gray-50 rounded-lg p-4 space-y-4 border border-gray-200">
              <h4 className="font-medium text-gray-700 mb-3">Preview Controls</h4>
              
              {/* Test Scenario Selector */}
              {setTestScenario && (
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-600 mb-2">Test Scenario</label>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { key: 'default', label: 'Default (3/10)' },
                      { key: 'almost-complete', label: 'Almost Complete' },
                      { key: 'milestone-ready', label: 'Milestone Ready' },
                      { key: 'all-milestones', label: 'All Complete' },
                      { key: 'near-expiry', label: 'Near Expiry' }
                    ].map((scenario) => (
                      <button
                        key={scenario.key}
                        type="button"
                        onClick={() => setTestScenario(scenario.key as any)}
                        className={`px-3 py-2 rounded text-xs font-medium transition-colors ${
                          testScenario === scenario.key
                            ? 'bg-indigo-100 text-indigo-800 border border-indigo-200'
                            : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                        }`}
                      >
                        {scenario.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Earned Stamps Slider */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-600">
                  Earned Stamps: {previewControls.earnedStamps} / {previewControls.totalStamps}
                </label>
                <input
                  type="range"
                  min="0"
                  max={previewControls.totalStamps}
                  value={previewControls.earnedStamps}
                  onChange={(e) => {
                    const newValue = parseInt(e.target.value);
                    setPreviewControls(prev => ({ 
                      ...prev, 
                      earnedStamps: newValue 
                    }));
                    // Reset test scenario to custom when manually changing stamps
                    if (setTestScenario && newValue !== getEarnedStampsForScenario(previewControls.totalStamps)) {
                      setTestScenario('default');
                    }
                  }}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer
                           [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4
                           [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-blue-600 [&::-webkit-slider-thumb]:cursor-pointer
                           [&::-webkit-slider-thumb]:shadow-md [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4
                           [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-blue-600 [&::-moz-range-thumb]:cursor-pointer
                           [&::-moz-range-thumb]:border-none [&::-moz-range-thumb]:shadow-md"
                />
              </div>

              {/* Toggles */}
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-600">Show Milestones</span>
                <button
                  type="button"
                  onClick={() => setPreviewControls(prev => ({ 
                    ...prev, 
                    showMilestones: !prev.showMilestones 
                  }))}
                  className="flex items-center gap-1 text-blue-600 hover:text-blue-700"
                >
                  {previewControls.showMilestones ? (
                    <BsToggleOn size={24} />
                  ) : (
                    <BsToggleOff size={24} />
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Dynamic Preview */}
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <h4 className="font-medium text-gray-700 mb-4 text-center">Live Preview</h4>
            <div className="flex justify-center">
              {(() => {
                const dynamicData = createDynamicPreviewData(
                  {
                    iconUrl,
                    iconFilledUrl,
                    iconWithRewardUrl,
                    iconWithRewardFilledUrl,
                    totalStamps: previewControls.totalStamps,
                    milestonePositions: previewControls.showMilestones ? [3, 7] : []
                  },
                  previewControls
                );
                
                // Create a theme key by hashing the theme object for re-rendering when theme changes
                const themeKey = watchedTheme ? JSON.stringify(watchedTheme) : 'default';
                
                return (
                  <StampCardIframe
                    key={`preview-${resetCounter}-${previewControls.earnedStamps}-${themeKey}`}
                    data={dynamicData}
                    theme={watchedTheme as any}
                  />
                );
              })()}
            </div>
          </div>

        </div>

        {/* Icon Customizer */}
        <div className="space-y-4">
          <h4 className="font-medium text-gray-700 flex items-center gap-2">
            <FaPalette className="text-purple-600" />
            Icon Customization
          </h4>
          <div className="flex justify-center items-center gap-4 flex-wrap">
          {iconConfigs.map((config, index) => (
            <React.Fragment key={config.id}>
              {index > 0 && <div className="w-8 border-t-2  border-gray-300"></div>}
              <div className="flex flex-col items-center gap-3">
                <button
                  type="button"
                  onClick={() => handleIconClick(config.id as keyof typeof fileInputRefs)}
                  className={`w-24 h-24 rounded-full border-4  border-gray-300 ${
                    config.bgColor
                  } flex items-center justify-center hover:border-gray-400 hover:scale-105 transition-all duration-200 cursor-pointer ${
                    uploadingType === config.id ? 'animate-pulse' : ''
                  }`}
                  disabled={uploadingType === config.id}
                >
                  {uploadingType === config.id ? (
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                  ) : config.value ? (
                    <img
                      src={config.value}
                      alt={config.label}
                      className="w-16 h-16 object-contain rounded-full"
                    />
                  ) : (
                    config.defaultIcon
                  )}
                </button>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${config.labelColor}`}>
                  {config.label}
                </span>
                <input
                  ref={fileInputRefs[config.id as keyof typeof fileInputRefs]}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleFileSelect(config.id, e.target.files)}
                />
              </div>
            </React.Fragment>
          ))}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-center gap-4 mt-6">
            <button
              type="button"
              onClick={handleReset}
              className="px-6 py-3 border-2 border-gray-300 text-gray-600 rounded-lg font-medium hover:bg-gray-50 transition-colors duration-200 flex items-center gap-2"
            >
              <FaUndo />
              Reset Icons
            </button>
          </div>
        </div>

        {/* Theme Customization */}
        <div className="space-y-4">
          <h4 className="font-medium text-gray-700 flex items-center gap-2">
            <MdColorLens className="text-indigo-600" />
            Theme Customization
          </h4>
          
          {/* Theme Presets */}
          <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-4 space-y-3">
            <h5 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
              <FaPaintBrush className="text-purple-600" />
              Premium Theme Presets
            </h5>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
              {Object.entries(themeConfig.presets).map(([key, preset]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => {
                    setSelectedPreset(key);
                    const theme = preset.theme;
                    setValue('metadata.theme.cardBackground', theme.cardBackground);
                    setValue('metadata.theme.textAndBorderColor', theme.textAndBorderColor);
                    setValue('metadata.theme.claimedStampColor', theme.claimedStampColor);
                    setValue('metadata.theme.nextStampHighlight', theme.nextStampHighlight);
                    setValue('metadata.theme.checkmarkColor', theme.checkmarkColor);
                    setValue('metadata.theme.borderPattern', theme.borderPattern as 'solid' | 'dashed' | 'dotted');
                  }}
                  className={`relative p-3 rounded-lg border-2 transition-all duration-200 hover:scale-105 ${
                    selectedPreset === key 
                      ? 'border-purple-500 bg-purple-50' 
                      : 'border-gray-200 bg-white hover:border-purple-300'
                  }`}
                  title={preset.description}
                >
                  <div className="flex items-center justify-center mb-2">
                    <div 
                      className="w-8 h-8 rounded-full border-2"
                      style={{ 
                        backgroundColor: preset.theme.cardBackground,
                        borderColor: preset.theme.textAndBorderColor
                      }}
                    />
                    <div 
                      className="w-4 h-4 rounded-full -ml-2"
                      style={{ 
                        backgroundColor: preset.theme.claimedStampColor,
                      }}
                    />
                  </div>
                  <span className="text-xs font-medium text-gray-700">{preset.name}</span>
                  {selectedPreset === key && (
                    <div className="absolute top-1 right-1 w-2 h-2 bg-purple-500 rounded-full" />
                  )}
                </button>
              ))}
              
              {/* Custom Option */}
              <button
                type="button"
                onClick={() => setSelectedPreset('custom')}
                className={`relative p-3 rounded-lg border-2 transition-all duration-200 hover:scale-105 ${
                  selectedPreset === 'custom' 
                    ? 'border-purple-500 bg-purple-50' 
                    : 'border-gray-200 bg-white hover:border-purple-300'
                }`}
                title="Custom theme colors"
              >
                <div className="flex items-center justify-center mb-2">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-gray-400 to-gray-600 border-2 border-gray-300" />
                </div>
                <span className="text-xs font-medium text-gray-700">Custom</span>
                {selectedPreset === 'custom' && (
                  <div className="absolute top-1 right-1 w-2 h-2 bg-purple-500 rounded-full" />
                )}
              </button>
            </div>
          </div>

          {/* Custom Theme Colors */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-4">
            {/* Primary Colors */}
            <div className="space-y-3">
              <h5 className="text-sm font-semibold text-gray-700">Primary Colors</h5>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {/* Card Background */}
                <div className="flex items-center justify-between gap-2">
                  <label className="text-sm text-gray-600">Card Background</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={cardBackground}
                      onChange={(e) => {
                      setValue('metadata.theme.cardBackground', e.target.value);
                      setSelectedPreset('custom');
                    }}
                      className="h-8 w-12 rounded border border-gray-300 cursor-pointer hover:border-indigo-500 transition-colors"
                    />
                    <span className="text-xs text-gray-500 font-mono">{cardBackground}</span>
                  </div>
                </div>

                {/* Text and Border Color */}
                <div className="flex items-center justify-between gap-2">
                  <label className="text-sm text-gray-600">Text & Border</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={textAndBorderColor}
                      onChange={(e) => {
                      setValue('metadata.theme.textAndBorderColor', e.target.value);
                      setSelectedPreset('custom');
                    }}
                      className="h-8 w-12 rounded border border-gray-300 cursor-pointer hover:border-indigo-500 transition-colors"
                    />
                    <span className="text-xs text-gray-500 font-mono">{textAndBorderColor}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Stamp Colors */}
            <div className="space-y-3">
              <h5 className="text-sm font-semibold text-gray-700">Stamp Colors</h5>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {/* Claimed Stamp Color */}
                <div className="flex items-center justify-between gap-2">
                  <label className="text-sm text-gray-600">Claimed Stamp</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={claimedStampColor}
                      onChange={(e) => {
                      setValue('metadata.theme.claimedStampColor', e.target.value);
                      setSelectedPreset('custom');
                    }}
                      className="h-8 w-12 rounded border border-gray-300 cursor-pointer hover:border-indigo-500 transition-colors"
                    />
                    <span className="text-xs text-gray-500 font-mono">{claimedStampColor}</span>
                  </div>
                </div>

                {/* Next Stamp Highlight */}
                <div className="flex items-center justify-between gap-2">
                  <label className="text-sm text-gray-600">Next Stamp Highlight</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={nextStampHighlight}
                      onChange={(e) => {
                      setValue('metadata.theme.nextStampHighlight', e.target.value);
                      setSelectedPreset('custom');
                    }}
                      className="h-8 w-12 rounded border border-gray-300 cursor-pointer hover:border-indigo-500 transition-colors"
                    />
                    <span className="text-xs text-gray-500 font-mono">{nextStampHighlight}</span>
                  </div>
                </div>

                {/* Checkmark Color */}
                <div className="flex items-center justify-between gap-2">
                  <label className="text-sm text-gray-600">Checkmark</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={checkmarkColor}
                      onChange={(e) => {
                      setValue('metadata.theme.checkmarkColor', e.target.value);
                      setSelectedPreset('custom');
                    }}
                      className="h-8 w-12 rounded border border-gray-300 cursor-pointer hover:border-indigo-500 transition-colors"
                    />
                    <span className="text-xs text-gray-500 font-mono">{checkmarkColor}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Border Pattern */}
            <div className="space-y-3">
              <h5 className="text-sm font-semibold text-gray-700">Border Style</h5>
              <div className="flex items-center justify-between gap-2">
                <label className="text-sm text-gray-600">Border Pattern</label>
                <select
                  value={borderPattern}
                  onChange={(e) => {
                    setValue('metadata.theme.borderPattern', e.target.value as 'solid' | 'dashed' | 'dotted');
                    setSelectedPreset('custom');
                  }}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="solid">Solid</option>
                  <option value="dashed">Dashed</option>
                  <option value="dotted">Dotted</option>
                </select>
              </div>
            </div>

            {/* Theme Reset Button */}
            <div className="flex justify-end pt-2">
              <button
                type="button"
                onClick={() => {
                  const classicTheme = themeConfig.presets.classic.theme;
                  setValue('metadata.theme.cardBackground', classicTheme.cardBackground);
                  setValue('metadata.theme.textAndBorderColor', classicTheme.textAndBorderColor);
                  setValue('metadata.theme.claimedStampColor', classicTheme.claimedStampColor);
                  setValue('metadata.theme.nextStampHighlight', classicTheme.nextStampHighlight);
                  setValue('metadata.theme.checkmarkColor', classicTheme.checkmarkColor);
                  setValue('metadata.theme.borderPattern', classicTheme.borderPattern as 'dashed' | 'solid' | 'dotted');
                }}
                className="px-4 py-2 text-sm border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
              >
                <FaUndo className="text-xs" />
                Reset to Defaults
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Hidden form fields to maintain form state */}
      <input type="hidden" {...register('iconUrl')} />
      <input type="hidden" {...register('iconFilledUrl')} />
      <input type="hidden" {...register('iconWithRewardUrl')} />
      <input type="hidden" {...register('iconWithRewardFilledUrl')} />
      <input type="hidden" {...register('metadata.theme.cardBackground')} />
      <input type="hidden" {...register('metadata.theme.textAndBorderColor')} />
      <input type="hidden" {...register('metadata.theme.claimedStampColor')} />
      <input type="hidden" {...register('metadata.theme.nextStampHighlight')} />
      <input type="hidden" {...register('metadata.theme.checkmarkColor')} />
      <input type="hidden" {...register('metadata.theme.borderPattern')} />
    </FormSection>
  );
};
